<template>
  <main class="row content__page">
    <section class="column large-full entry format-standard">
      <div class="content__page-header">
        <h1>
          <i class="fa fa-envelope" aria-hidden="true"></i>
          {{ $t('subscription.title') }}
        </h1>
      </div>

      <el-form ref="formRef" :model="form" :rules="rules" label-position="top" class="subscription-form">
        <!-- First Name & Last Name on same row -->
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item :label="$t('form_labels.first_name')" prop="first_name">
              <el-input v-model="form.first_name" :placeholder="$t('form_labels.first_name_placeholder')" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('form_labels.last_name')" prop="last_name">
              <el-input v-model="form.last_name" :placeholder="$t('form_labels.last_name_placeholder')" />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- Email -->
        <el-form-item 
            prop="email"
            :validate-status="computedEmailStatus">
          <template #label>
            {{ $t('form_labels.email') }}
          </template>

          <el-input v-model="form.email" :placeholder="$t('form_labels.email_placeholder')" />

          <template #error v-if="fieldErrors != null && fieldErrors.email != null && fieldErrors.email !== ''">
            <div class="el-form-item__error">
              {{ fieldErrors.email }}
            </div>
          </template>

        </el-form-item>

        <!-- Frequency -->
        <el-form-item :label="$t('form_labels.frequency')" prop="frequency">
          <el-radio-group v-model="form.frequency">
            <el-radio label="daily">{{ $t('subscription.daily') }}</el-radio>
            <el-radio label="weekly">{{ $t('subscription.weekly') }}</el-radio>
            <el-radio label="monthly">{{ $t('subscription.monthly') }}</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- Categories -->
        <el-form-item :label="$t('form_labels.categories')" prop="categories">
          <el-select v-model="form.categories" multiple :placeholder="$t('form_labels.select_categories')">
            <el-option v-for="category in availableCategories" :key="category.id" :label="category.name" :value="category.id" />
          </el-select>
        </el-form-item>

        <div class="form-actions">
          <el-button @click="$router.push({ name: 'HomePage' })">{{ $t('buttons.cancel') }}</el-button>
          <el-button type="secondary" @click="validateAndSubmit">{{ $t('subscription.subscribe') }}</el-button>
        </div>
      </el-form>
    </section>
  </main>
</template>

<script>
import { ref, reactive, onMounted, computed, nextTick, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from "vue-i18n";
import { ElNotification } from "element-plus";
import { CREATE_NEWSLETTER_SUBSCRIPTION } from "@/graphql/newsletter-subscriptions";
import { GET_VISIBLE_CATEGORIES } from "@/graphql/categories";
import apolloClient from "@/services/apollo-client";

export default {
  name: 'SubscribePage',
  setup() {
    const router = useRouter();
    const { t } = useI18n();
    const availableCategories = ref([]);
    const fieldErrors = reactive({ email: '' });
    const formRef = ref(null);

    const form = reactive({
      first_name: "",
      last_name: "",
      email: "",
      frequency: "weekly",
      categories: []
    });

    const rules = reactive({
      first_name: [
        { required: true, message: t("form_rules.first_name_required"), trigger: "blur" }
      ],
      last_name: [
        { required: true, message: t("form_rules.last_name_required"), trigger: "blur" }
      ],
      email: [
        { required: true, message: t("form_rules.email_required"), trigger: "blur" },
        { type: "email", message: t("form_rules.email_invalid"), trigger: ["blur", "change"] },
        {
          validator: (_, __, callback) => {
            if (fieldErrors.email) {
              callback(new Error(fieldErrors.email));
            } else {
              callback();
            }
          },
          trigger: 'blur',
          message: '' // optional fallback
        }
      ],
      frequency: [
        { required: true, message: t("form_rules.frequency_required"), trigger: "change" }
      ],
      categories: [
        { required: true, message: t("form_rules.categories_required"), trigger: "change" }
      ]
    });

    // This comes from Element Plus form instance
    const emailFieldRef = computed(() =>
      formRef.value?.fields?.find((f) => f.prop === 'email')
    );

    // Combine internal + external errors
    const emailFieldError = computed(() =>
      fieldErrors.email || emailFieldRef.value?.validateMessage || ''
    );

    // Ensure the red border shows if any error exists
    const computedEmailStatus = computed(() =>
      emailFieldError.value ? 'error' : ''
    );

    const fetchCategories = async () => {
      try {
        const { data } = await apolloClient.query({ query: GET_VISIBLE_CATEGORIES });
        availableCategories.value = data.visibleCategories;
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    const validateAndSubmit = () => {
      formRef.value.validate(async (valid) => {
        if (!valid) {
          return;
        }

        try {
          const { data } = await apolloClient.mutate({
            mutation: CREATE_NEWSLETTER_SUBSCRIPTION,
            variables: { 
              input: { 
                first_name: form.first_name,
                last_name: form.last_name,
                email: form.email, 
                frequency: form.frequency, 
                categories: form.categories
              }
            }
          });

          const response = data?.createNewsletterSubscription;

          if (response?.success) {
            switch (response.reason) {
              case "USER_EMAIL_VERIFICATION_PENDING":
                ElNotification({
                  title: t("notifications.pending.title"),
                  message: t("notifications.pending.verify_email"),
                  type: "warning"
                });
                break;

              case "NEWSLETTER_SUBSCRIPTION_CREATED":
                ElNotification({
                  title: t("notifications.success.title"),
                  message: t("notifications.subscriptions.success"),
                  type: "success"
                });
                router.push({ name: 'HomePage' });
                break;
            }
          } else {
            switch (response.reason) {
              case 'NEWSLETTER_EMAIL_ALREADY_EXISTS':

              ElNotification({
                  title: t("notifications.error.title"),
                  message: t("form_rules.email_already_exists"),
                  type: "error"
                });

                fieldErrors.email = t('form_rules.email_already_exists');
                await nextTick(); // ensures the DOM updates
                await formRef.value.validateField('email'); // Re-triggers the validator
                break;
            }
          }
        } catch (error) {
          console.error("Subscription error:", error);
          // ElNotification({
          //   title: t("notifications.error.title"),
          //   message: t("notifications.subscriptions.error"),
          //   type: "error"
          // });
        }
      });
    };

    onMounted(fetchCategories);


    watch(() => form.email, () => {
  if (fieldErrors.email) {
    fieldErrors.email = '';
    nextTick(() => {
      formRef.value?.validateField('email'); // re-run validation
    });
  }
});


    return {
      router,
      formRef,
      form,
      rules,
      availableCategories,
      validateAndSubmit,
      fieldErrors,
      emailFieldError,
      computedEmailStatus,
      emailFieldRef,
    };
  }
};
</script>

<style scoped>
.subscription-form {
  margin-top: 2rem;
}
.form-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}
</style> 